<script setup>
import { ref, computed, inject } from 'vue';
import _ from 'lodash'
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification"
import SubscriptionService from '@/services/SubscriptionService'
import { track, EVENTS, getAnalyticsIdentity } from '@/lib/tracking'
import SubscribePlanItemHeader from './SubscribePlanItemHeader.vue';
import SubscribePlanItemDescription from './SubscribePlanItemDescription.vue';
import SubscribeBillingAddress from './SubscribeBillingAddress.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import IconBase from '../../components/icon/IconBase.vue';
import IconChevronLeft from '../../components/icon/IconChevronLeft.vue';
import IconInfoSmall from '../../components/icon/IconInfoSmall.vue';
import FormInput from '../../components/Atoms/FormInput.vue';
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import IconChevronDown from '../../components/icon/IconChevronDown.vue';
import IconCross from '../../components/icon/IconCross.vue';
import { date } from "@/filtersNew"

const props = defineProps({
  subscriptionPackage: {
    type: Object,
  },
  isYearly: {
    type: Boolean
  }
})

const { notify } = useNotification()
const store = useStore()

const getChangeDirection = (pack) => {
  const currentPackage = store.getters['User/baseSubscription']?.package
  if (!currentPackage) return 'new'
  const toMonthly = (p) => (p?.cycleAge === 365 ? (p?.baseCharge || 0) / 12 : (p?.baseCharge || 0))
  const next = toMonthly(pack)
  const current = toMonthly(currentPackage)
  if (next > current) return 'upgrade'
  if (next < current) return 'downgrade'
  return 'same'
}

const isYearly = computed(() =>
  props.subscriptionPackage?.cycleAge != null
    ? props.subscriptionPackage.cycleAge === 365
    : props.isYearly
)

const price = computed(() => isYearly.value ? props.subscriptionPackage?.info?.yearly : props.subscriptionPackage?.baseCharge)

const { selectPackage, availablePaymentMethods, selectedPaymentMethod, selectPaymentMethod } = inject('selectedPaymentMethod')

const availablePaymentMethodsFiltered = computed(() => price.value > 50 ? availablePaymentMethods.value.filter(method => method.value !== 'paypal') : availablePaymentMethods.value)

const paymentTypeDropdown = ref(null)

const showCouponCode = ref(false)
const couponCode = ref({
  code: '',
  status: null,
  isValid: false
})

const isDisabled = computed(() => couponCode.value.code.length === 0)

const billingAddressForm = ref(null)
const billingAddress = ref({ street: '', city: '', state: '', zip: '', country: '' })
const showBillingAddressErrors = ref(false)
const isSubmitting = ref(false)

const isAnnualPackage = (pack) => {
  return /yearly/i.test(pack.name)
}

const billingCadenceLabel = computed(() =>
  isAnnualPackage(props.subscriptionPackage) ? 'Billed annually' : 'Billed monthly'
)
const getEndingDate = (pack) => {
  const today = new Date()
  if (isAnnualPackage(props.subscriptionPackage)) {
    today.setFullYear(today.getFullYear() + 1)
  } else {
    today.setMonth(today.getMonth() + 1)
  }
  return today
}

const closeCouponCode = () => {
  showCouponCode.value = false
  couponCode.value.code = ''
  couponCode.value.status = null
  couponCode.value.isValid = false
}

const selectPayment = (paymentMethod) => {
  selectPaymentMethod(paymentMethod)
  paymentTypeDropdown.value.toggleDropdown()
}

const applyCouponCode = async () => {
  try {
    if (couponCode.value.code.length === 0) {
      couponCode.value.status = 'The coupon code cannot be empty'
      return
    }
    const data = await SubscriptionService.checkCouponCode(props.subscriptionPackage._id, selectedPaymentMethod.value, couponCode.value.code)
    if (data.status === 'valid_code') {
      couponCode.value.isValid = true
      couponCode.value.status = "Promo code successfully redeemed. You\'ll see the discounted price on the next page"
    } else {
      couponCode.value.isValid = false
      couponCode.value.status = 'Your promo code is no longer valid'
    }
  } catch (e) {
    notify({ group: 'error', text: e ? e.message : 'Failed to check coupon code' })
  }
}

const requestCheckout = async () => {
  if (isSubmitting.value) return
  if (!billingAddressForm.value?.isComplete) {
    showBillingAddressErrors.value = true
    return
  }
  isSubmitting.value = true
  try {
    // Validate coupon code first
    if (couponCode.value.code) {
      await applyCouponCode()
      if (!couponCode.value.isValid) {
        notify({ group: 'error', text: couponCode.value.status })
        isSubmitting.value = false
        return
      }
    }
    // Create invoice
    const invoiceRes = await SubscriptionService.createInvoicePaymentRequest(props.subscriptionPackage._id, selectedPaymentMethod.value.value, couponCode.value.code, billingAddress.value)
    const pack = props.subscriptionPackage
    track(EVENTS.CHECKOUT_STARTED, {
      packageId: pack._id,
      planName: pack.info?.name || pack.name,
      planCategory: pack.category,
      billingCycle: pack.cycleAge === 365 ? 'yearly' : 'monthly',
      baseCharge: pack.baseCharge,
      paymentMethod: selectedPaymentMethod.value?.value,
      hasCoupon: !!couponCode.value.code,
      changeDirection: getChangeDirection(pack),
    })
    // Carry the PostHog identity into the off-app aMember/Stripe checkout so the
    // purchase ("Checkout Completed") can be attributed to the same user +
    // session and close the revenue funnel.
    let checkoutUrl = invoiceRes.invoiceUrl
    try {
      const { distinctId, sessionId } = getAnalyticsIdentity()
      const u = new URL(invoiceRes.invoiceUrl)
      if (distinctId) u.searchParams.set('ph_distinct_id', distinctId)
      if (sessionId) u.searchParams.set('ph_session_id', sessionId)
      checkoutUrl = u.toString()
    } catch (err) {
      /* non-standard URL — fall back to the raw invoice URL */
    }
    window.location.href = checkoutUrl
  } catch (e) {
    isSubmitting.value = false
    notify({ group: 'error', text: e ? e.message : 'Unable to create the invoice. Please try again.' })
  }
}

</script>

<template>
  <div class="max-w-[820px] mx-auto">
    <FormButton @click="selectPackage?.(0)"
      custom-classes="inline-flex items-center gap-1.5 h-8 pl-2 pr-3 mb-3 rounded-lg bg-surface-2 text-surface-8 hover:text-white hover:bg-surface-3 transition-colors text-sm font-medium">
      <icon-base fill="none">
        <icon-chevron-left />
      </icon-base>
      Back
    </FormButton>
    <section class="bg-surface-1 p-3 rounded-xl flex flex-wrap lg:flex-nowrap w-full gap-3 mt-2">
      <div class="rounded-lg w-full lg:w-1/3 overflow-hidden shadow-lg">
        <SubscribePlanItemHeader is-cart :plan="subscriptionPackage" :is-yearly="isYearly" />
        <SubscribePlanItemDescription is-cart :plan="subscriptionPackage" :is-yearly="isYearly" />
      </div>
      <div class="min-w-0 !p-4 lg:!p-6 grow bg-surface-2 rounded-lg shadow-lg">
        <h2 class="text-lg font-semibold mt-2 lg:mt-0">Order summary</h2>
        <p class="text-sm text-surface-7 mt-1 mb-4">Review your plan before checkout.</p>
        <div class="relative lg:min-h-[3rem] mb-4">
          <FormButton v-if="!showCouponCode" @click="showCouponCode = !showCouponCode"
            customClasses="text-main hover:text-main-light text-sm font-medium h-8 !p-0">Got a coupon code?
          </FormButton>
          <div class="grid grid-cols-[minmax(0,1fr)_auto] sm:flex gap-2 items-center" v-else>
            <FormInput v-model="couponCode.code" placeholder="Enter code" class="min-w-0 sm:grow" />
            <FormButton :disabled="isDisabled" @click="applyCouponCode" class="shrink-0" label="Apply" />
            <FormButton @click="closeCouponCode" title="Cancel"
              custom-classes="col-span-full sm:col-span-1 sm:shrink-0 w-full sm:size-8 h-8 flex items-center justify-center rounded-md text-surface-7 hover:text-white hover:bg-surface-3 transition-colors">
              <icon-base fill="none">
                <icon-cross />
              </icon-base>
            </FormButton>
          </div>
          <p class="text-tiny flex leading-4 !mt-2" v-if="couponCode.status"
            :class="couponCode.isValid ? 'text-success' : 'text-danger'">
            <icon-base v-if="!couponCode.isValid" class="mr-1">
              <icon-info-small />
            </icon-base>
            {{ couponCode.status }}
          </p>
        </div>
        <SubscribeBillingAddress ref="billingAddressForm" v-model="billingAddress"
          :show-errors="showBillingAddressErrors" />
        <div class="rounded-xl bg-surface-1 p-5">
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span class="text-surface-7">Renews on</span>
              <span class="text-surface-9 font-medium">{{ date(getEndingDate(props.subscriptionPackage), 'DD MMM, YYYY') }}</span>
            </div>
            <div class="flex flex-wrap items-center justify-between gap-2 text-sm min-h-8">
              <span class="text-surface-7">Pay with</span>
              <span v-if="availablePaymentMethodsFiltered.length <= 1" class="text-surface-9 font-medium capitalize">
                {{ selectedPaymentMethod.label }}
              </span>
            <drop-down-menu v-else ref="paymentTypeDropdown" position="right" no-popper
              wrapperClass="bg-surface-3 !border !border-surface-3 min-w-[150px] mt-1"
              customClasses="text-tiny disabled:text-surface-7 h-8 bg-surface-3 rounded-md flex items-center px-3 capitalize text-nowrap !border !border-surface-3">
              <template #toggle-button>
                {{ selectedPaymentMethod.label }}
                <icon-base class="ml-2">
                  <icon-chevron-down />
                </icon-base>
              </template>
              <template #menu-items>
                <drop-down-menu-item
                  customClasses="w-full bg-surface-3 text-white text-tiny text-left font-medium h-8 px-3 whitespace-nowrap hover:!bg-surface-4"
                  v-for="(paymentMethod, index) in availablePaymentMethodsFiltered" :key="index"
                  @click="selectPayment(paymentMethod)">
                  {{ paymentMethod.label }}
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
            </div>
          </div>
          <div class="flex items-end justify-between pt-4 mt-2">
            <div>
              <p class="font-semibold text-surface-9 leading-tight">Total</p>
              <p class="text-tiny text-surface-6">{{ billingCadenceLabel }}</p>
            </div>
            <p class="text-2xl font-bold leading-none">${{ props.subscriptionPackage.baseCharge }}</p>
          </div>
        </div>
        <FormButton class="!my-4 text-center justify-center" full-width size="lg" label="Proceed to Checkout"
          :is-loading="isSubmitting" :disabled="isSubmitting" @click="requestCheckout" />
        <p class="text-surface-6 text-sm text-center">* All prices are in USD</p>
      </div>
    </section>
  </div>
</template>

<style></style>
